import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { RecurrenceEditor } from '@syncfusion/ej2-angular-schedule';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ScheduledBooking } from 'src/app/simulator-booking/models/scheduled-booking';
import { ScheduledBookingTypeEnum, ScheduledBookingTypeEnumLabel } from 'src/app/shared/models/scheduled-booking-type.enum';
import { BaseModalComponent } from 'src/app/core/base-modal.componant';
import { Simulator } from 'src/app/simulator-booking/models/simulators';
import { SessionService } from 'src/app/shared/services/session.service';
import { AlertModalComponent } from 'src/app/shared/modals/alert-modal/alert-modal.component';
import { ScheduledBookingService } from 'src/app/simulator-booking/services/scheduled-booking.service';
import { LeagueService } from 'src/app/simulator-booking/services/league.service';
import { LeagueIterationService } from 'src/app/simulator-booking/services/league-iteration.service';
import { League } from 'src/app/simulator-booking/models/league';
import { LeagueIteration } from 'src/app/simulator-booking/models/league-iteration';
import moment from 'moment';
import { environment } from 'src/environments/environment';
import { UserData } from 'src/app/shared/models/user.model';


@Component({
  selector: 'create-scheduled-booking-modal',
  templateUrl: 'create-scheduled-booking-modal.component.html',
  styleUrls: ['./create-scheduled-booking-modal.component.scss'],
  standalone: false
})
export class CreateScheduledBookingModalComponent extends BaseModalComponent implements OnInit {
  @ViewChild('recurrenceObj') recurrenceObj!: RecurrenceEditor;

  recurrenceForm: FormGroup;
  recurrenceRule: string = '';
  selectedStartDate: Date;
  selectedEndDate: Date;
  selectedSimulators: any[] = [];
  scheudledBookingTypes: any[] = ScheduledBookingTypeEnumLabel;
  simulators: Simulator[] = [];
  leagues: League[] = [];
  leagueIterations: LeagueIteration[] = [];
  enabledStates = {
    startDate: true,
    endDate: true,
    startTime: true,
    endTime: true,
    name: true,
    price: true
  };
  userDetails: UserData;


  constructor(
    private dialogRef: MatDialogRef<CreateScheduledBookingModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ScheduledBooking,
    private formBuilder: FormBuilder,
    private scheduledBookingService: ScheduledBookingService,
    private sessionService: SessionService,
    private leagueIterationService: LeagueIterationService,
    private leagueService: LeagueService,
    private dialog: MatDialog
  ) {
    super();
    this.recurrenceForm = this.formBuilder.group({
      startDateOnly: ['', Validators.required],
      endDateOnly: ['', Validators.required],
      startTimeOnly: ['', Validators.required],
      endTimeOnly: ['', Validators.required],
      name: ['', Validators.required],
      price: ['', [Validators.required, Validators.min(0)]],
      simulatorIds: [[], Validators.required],
      type: [''],
      recurrenceRule: [''],
      referenceId: [null],
      iterationId: [''],
      leagueId: [''],
      customerEmail: [''],
      sendPaymentLink: [false],
      successUrl: [`${environment.dashboardLocation}/payment-success-failure`],
      creditCardFeeType: [],
    });

    this.selectedStartDate = data.startTime || new Date();
    this.selectedEndDate = data.endTime || new Date();
    this.selectedSimulators = this.simulators || [];
  }

  ngOnInit(): void {
    this.recurrenceForm.patchValue({
      startDateOnly: this.selectedStartDate,
      endDateOnly: this.selectedEndDate,
      startTimeOnly: null,
      endTimeOnly: null,
      name: this.data.name || '',
      price: this.data.price || 0,
      simulatorIds: this.selectedSimulators.map(sim => sim.id) || [],
      type: this.data.type || null,
      customerEmail: this.data.customerEmail || '',
      sendPaymentLink: false
    });

    if (this.data.simulatorIds && this.simulators) {
      this.selectedSimulators = this.simulators.filter(sim =>
        this.data.simulatorIds!.includes(sim.id)
      );
    }

    this.userDetails = this.sessionService.getUser();
  }
  onStartDateChange(date: Date): void {
    this.selectedStartDate = date;
    this.recurrenceForm.patchValue({
      startDate: date
    });
  }

  onEndDateChange(date: Date): void {
    this.selectedEndDate = date;
    this.recurrenceForm.patchValue({
      endDate: date
    });
  }

  onRecurrenceChange(args: any): void {
    this.recurrenceRule = this.recurrenceObj.getRecurrenceRule();
    this.recurrenceForm.patchValue({
      recurrenceRule: this.recurrenceRule
    });
  }

  onSendPaymentLinkChange(args: any): void {
    const emailControl = this.recurrenceForm.get('customerEmail');
    if (args.checked) {
      emailControl?.setValidators([Validators.required, Validators.email]);
    } else {
      emailControl?.clearValidators();
      emailControl?.setValue('');
    }
    emailControl?.updateValueAndValidity();
  }


  onSelectedAllSimulators(args: any): void {
    this.selectedSimulators = args.value;
    this.recurrenceForm.patchValue({
      simulatorIds: args.value.map((s: any) => s && s.id ? s.id : s)
    });
  }

  onSimulatorChange(args: any): void {
    try {
      if (args && args.value && Array.isArray(args.value)) {
        this.selectedSimulators = args.value;
        const simulatorIds = args.value.map((s: any) => s && s.id ? s.id : s);
        this.recurrenceForm.patchValue({
          simulatorIds: simulatorIds
        });
      } else if (args && args.value === null) {
        this.selectedSimulators = [];
        this.recurrenceForm.patchValue({
          simulatorIds: []
        });
      }
    } catch (error) {
      console.error('Error in onSimulatorChange:', error);
      this.selectedSimulators = [];
      this.recurrenceForm.patchValue({
        simulatorIds: []
      });
    }
  }

  onScheduledBookingTypeChange(args: any): void {
    this.recurrenceForm.patchValue({
      type: args.itemData.type
    });

    if(args.itemData.type === ScheduledBookingTypeEnum.League){
      this.getLeagues();
    }else if(args.previousItemData.type === ScheduledBookingTypeEnum.League){
      this.leagues = [];
      this.leagueIterations = [];
      this.recurrenceForm.patchValue({
        referenceId: null,
        iterationId: null,
        leagueId: null,
        name: null,
        price: null,
        recurrenceRule: ''
      });

      this.recurrenceObj.setRecurrenceRule('');
      this.enabledStates.name = true;
      this.enabledStates.price = true;
      this.enabledStates.startDate = true;
      this.enabledStates.endDate = true;
      this.enabledStates.startTime = true;
      this.enabledStates.endTime = true;
    }
  }


  getLeagues(): void {
    this.leagueService.getAllByOrganization({
      organizationId: this.sessionService.getUser().organizationId
    }).subscribe({
      next: (result: any) => {
        this.leagues = result.results || [];
      },
      error: (error: any) => {
        console.error('Error loading leagues:', error);
      }
    });
  }

  getLeaguesIterations(args: any): void {
    let filter = {
      leagueId: args.itemData.id,
      text: '',
      page: 1,
      pageSize: 1000
    };

    this.leagueIterationService.getAllByLeagueId(filter).subscribe({
      next: (result: any) => {
        this.leagueIterations = result.results || [];
        this.recurrenceForm.get('name').setValue(args.itemData.name);
        this.enabledStates.name = false;
      },
      error: (error: any) => {
        console.error('Error loading iterations:', error);
      }
    });
  }


  onIterationChange(args: any): void {
    this.recurrenceForm.patchValue({
      iterationId: args.itemData.id
    });

    this.selectedStartDate = moment.utc(args.itemData.startDate).tz(this.sessionService.getUser().timeZone).toDate();
    this.selectedEndDate = moment.utc(args.itemData.endDate).tz(this.sessionService.getUser().timeZone).toDate();
    this.recurrenceForm.patchValue({
      startDateOnly: this.selectedStartDate,
      endDateOnly: this.selectedEndDate,
    });

    this.recurrenceForm.get('price').setValue(args.itemData.entryFee);

    const startTimeOnly = moment.utc(args.itemData.startDate).tz(this.sessionService.getUser().timeZone);
    const endTimeOnly = moment.utc(args.itemData.endDate).tz(this.sessionService.getUser().timeZone);

    this.recurrenceForm.patchValue({
      startTimeOnly: startTimeOnly.format('h:mm A'),
      endTimeOnly: endTimeOnly.format('h:mm A')
    });

    this.recurrenceForm.patchValue({
      referenceId: args.itemData.id
    });

    this.enabledStates.startDate = false;
    this.enabledStates.endDate = false;
    this.enabledStates.startTime = false;
    this.enabledStates.endTime = false;
    this.enabledStates.price = false;
  }

  onSave(): void {
    if (this.recurrenceForm.valid) {
      const startDate = this.selectedStartDate;
      const endDate = this.selectedEndDate;
      
      const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const endDateOnly = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
      const daysDifference = Math.ceil((endDateOnly.getTime() - startDateOnly.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDifference > 1 && !this.recurrenceRule) {
        const dialogRef = this.dialog.open(AlertModalComponent, {
          width: '40vw',
        });

        dialogRef.componentInstance.title = 'Confirm Multi-Day Booking';
        dialogRef.componentInstance.body = `<h3 class="mat-h3">This booking spans ${daysDifference} days.</h3>
        <p>Are you sure you want to create a booking that extends past a single day?</p>
        <p><strong>Recommendation:</strong> Consider using a recurrence rule instead to create multiple single-day bookings.</p>`;
        dialogRef.componentInstance.submitText = 'Yes, Create Booking';
        dialogRef.componentInstance.otherText = 'Cancel';
        dialogRef.componentInstance.isOtherTextDisabled = false;

        dialogRef.afterClosed().subscribe((response) => {
          if (response === 'Yes, Create Booking') {
            this.proceedWithSave();
          }
        });
        return;
      }
      
      this.proceedWithSave();
    } else {
      this.setError(null, this.recurrenceForm);
    }
  }

  private proceedWithSave(): void {
    this.isLoading = true;
    this.recurrenceForm.removeControl('iterationId');
    this.recurrenceForm.removeControl('leagueId');
    const formData = this.recurrenceForm.value;

    const startDate = this.selectedStartDate;
    const endDate = this.selectedEndDate;

    formData.startDateOnly = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
    formData.endDateOnly = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;

    formData.price = parseFloat(formData.price) || 0;

    if (formData.recurrenceRule && formData.recurrenceRule.endsWith(';')) {
      formData.recurrenceRule = formData.recurrenceRule.slice(0, -1);
    }

    if(formData.type !== ScheduledBookingTypeEnum.League){
      formData.startTimeOnly = this.formatTimeOnly(this.recurrenceForm.get('startTimeOnly').value);
      formData.endTimeOnly = this.formatTimeOnly(this.recurrenceForm.get('endTimeOnly').value);
    }else{
      formData.startTimeOnly = moment(this.recurrenceForm.get('startTimeOnly').value, ['h:mm A']).format('HH:mm:ss');
      formData.endTimeOnly = moment(this.recurrenceForm.get('endTimeOnly').value, ['h:mm A']).format('HH:mm:ss');
    }

    // Only include customerEmail if sendPaymentLink is enabled
    if (!formData.sendPaymentLink) {
      delete formData.customerEmail;
    }

    formData.organizationId = this.sessionService.getUser().organizationId;
    formData.createdBy = this.sessionService.getUserId();
    formData.timeZone = this.sessionService.getUser().timeZone;

    this.scheduledBookingService.create(formData).subscribe({
      next: (result) => {
        this.isLoading = false;
        if (result !== null || result !== undefined) {
          this.dialogRef.close({
            action: 'save',
            data: formData
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        this.setErrorV2(error, this.recurrenceForm);
        console.error(error);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close({
      action: 'cancel'
    });
  }

  onGenerateRule(): void {
    if (this.recurrenceObj) {
      this.recurrenceRule = this.recurrenceObj.getRecurrenceRule();
      this.recurrenceForm.patchValue({
        recurrenceRule: this.recurrenceRule
      });
    }
  }

  private formatTimeOnly(value: any): string {
    // Accept Date, time string (HH:mm or HH:mm:ss), or ISO/string parseable by Date
    if (value instanceof Date) {
      const hh = String(value.getHours()).padStart(2, '0');
      const mm = String(value.getMinutes()).padStart(2, '0');
      const ss = String(value.getSeconds()).padStart(2, '0');
      return `${hh}:${mm}:${ss}`;
    }

    if (typeof value === 'string') {
      const timeMatch = value.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?$/);
      if (timeMatch) {
        const hh = String(Math.min(23, parseInt(timeMatch[1], 10))).padStart(2, '0');
        const mm = String(Math.min(59, parseInt(timeMatch[2], 10))).padStart(2, '0');
        const ss = String(Math.min(59, timeMatch[3] ? parseInt(timeMatch[3], 10) : 0)).padStart(2, '0');
        return `${hh}:${mm}:${ss}`;
      }
      const asDate = new Date(value);
      if (!isNaN(asDate.getTime())) {
        const hh = String(asDate.getHours()).padStart(2, '0');
        const mm = String(asDate.getMinutes()).padStart(2, '0');
        const ss = String(asDate.getSeconds()).padStart(2, '0');
        return `${hh}:${mm}:${ss}`;
      }
    }
    
    // Unknown structure (e.g., null), default to 00:00:00
    return '00:00:00';
  }
}
